import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import some from 'lodash/some';
import messages from 'modules/booking/messages';
import { ReactComponent as UsersJoinIcon } from 'img/icons/user-join.svg';
import { ActionItem } from 'common/components';
import {
  EventAttendeeType,
  EventOverbookingType as OverbookingType,
  IEventPersonEntity,
} from 'modules/booking/interfaces';
import { isLimitedEvent, restrictModifyEventByTime } from 'utils/booking.utils';
import ActionItemWarningIconByTime from 'common/components/ActionsMenu/ActionItemWarningIconByTime';
import ActionItemWarningIcon from 'common/components/ActionsMenu/ActionItemWarningIcon';
import BookingActionItemContainer from 'components/Booking/BookingActionItemContainer';

interface IBookingActionItemJoin {
  userId: number;
  isEventCanceled: boolean;
  isAllowSelfBooking: boolean;
  spotLimited: boolean;
  bookedSpots: number;
  spotAmount: number;
  eventPersons: IEventPersonEntity[];
  eventParticipantTypes: EventAttendeeType[];
  allowedAttendeeTypes: EventAttendeeType[];
  overbookingType: OverbookingType;
  secondsBeforeEvent: number;
  joinRestrictionBeforeDays?: number | null;
  onClick: () => void;
}

const BookingActionItemJoin: FC<IBookingActionItemJoin> = ({
  userId,
  isEventCanceled,
  isAllowSelfBooking,
  spotLimited,
  bookedSpots,
  spotAmount,
  eventPersons,
  eventParticipantTypes,
  allowedAttendeeTypes,
  overbookingType,
  secondsBeforeEvent,
  joinRestrictionBeforeDays,
  onClick,
}) => {
  const intl = useIntl();
  let disableReason: JSX.Element | null = null;

  const allowJoinByAttendeeType = (): boolean => {
    for (const allowedAttendeeType of allowedAttendeeTypes) {
      if (eventParticipantTypes.includes(allowedAttendeeType)) {
        return true;
      }
    }

    return false;
  };

  const isUserInWaitList = (): boolean => {
    const person = eventPersons.find(item => item.customer?.id === userId);
    return person && person.waitList ? person.waitList : false;
  };

  const isUserInEvent = (): boolean => {
    return some(eventPersons, person => person.customer?.id === userId);
  };

  const isLimited = (): boolean => {
    return isLimitedEvent(overbookingType, bookedSpots, spotAmount, spotLimited);
  };

  if (isEventCanceled) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.eventIsCanceled)} />
    );
  } else if (!isAllowSelfBooking) {
    disableReason = (
      <ActionItemWarningIcon
        tooltipMessage={intl.formatMessage(messages.selfBookingIsNotAllowed)}
      />
    );
  } else if (!allowJoinByAttendeeType()) {
    disableReason = (
      <ActionItemWarningIcon
        tooltipMessage={intl.formatMessage(messages.restrictionByAttendeeType)}
      />
    );
  } else if (isUserInWaitList()) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.userInWaitList)} />
    );
  } else if (isUserInEvent()) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.userInEvent)} />
    );
  } else if (isLimited()) {
    disableReason = (
      <ActionItemWarningIcon tooltipMessage={intl.formatMessage(messages.limitedSpots)} />
    );
  } else if (restrictModifyEventByTime(secondsBeforeEvent, joinRestrictionBeforeDays)) {
    disableReason = (
      <ActionItemWarningIconByTime tooltipMsgType="restrictJoin" days={joinRestrictionBeforeDays} />
    );
  }

  const disabled = !!disableReason;

  return (
    <ActionItem
      key="confirmParticipationAction"
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      icon={<UsersJoinIcon />}
    >
      <BookingActionItemContainer message={intl.formatMessage(messages.joinClass)}>
        {disableReason}
      </BookingActionItemContainer>
    </ActionItem>
  );
};

export default BookingActionItemJoin;
